import {Alert, MenuItemConfig, MenuItemWithSelection, TextRegular} from '@hm/fundamental'
import {mdiAccountCircleOutline} from '@mdi/js'
import {TFunction} from 'i18next'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {Persona} from '../../../api/types/personas'
import {useFeaturesMenu} from '../../../app/welcomeFlow/useFeaturesMenu'
import {useActivePersona, usePersonas} from '../../../app/welcomeFlow/usePersonas'

interface UseRoleMenuItemParams {
  onSelectPersona: (persona: Persona) => void
}

interface RoleSelectionMenuProps {
  activePersonaId: number
  personas: Persona[]
  onSelectPersona: (personaId: string) => void
  t: TFunction
}

const RoleSelectionMenu: React.FC<RoleSelectionMenuProps> = ({
  activePersonaId,
  personas,
  onSelectPersona,
  t
}) => (
  <div className="flex flex-col">
    <Alert variant="warning" className="mx-3 mt-3">
      <TextRegular>{t('suite.roleSwitch.warning')}</TextRegular>
    </Alert>
    <div className="max-h-[360px] overflow-y-auto mt-3">
      {personas.map((persona) => (
        <MenuItemWithSelection
          key={persona.id}
          itemKey={persona.id.toString()}
          title={t(`suite.personas.${persona.name}`, {defaultValue: persona.name})}
          selected={persona.id === activePersonaId}
          onClicked={onSelectPersona}
          icon={null}
        />
      ))}
    </div>
  </div>
)

export const useRoleMenuItem = ({onSelectPersona}: UseRoleMenuItemParams) => {
  const {t} = useTranslation()
  const {personas = [], isLoading: arePersonasLoading} = usePersonas()
  const {activePersona, isLoading: isActivePersonaLoading} = useActivePersona()
  const {isFeatureMenuNotFound} = useFeaturesMenu()

  const selectPersona = useCallback(
    (personaId: string) => {
      const selectedPersona = personas.find((persona) => persona.id.toString() === personaId)

      if (!selectedPersona || selectedPersona.id === activePersona?.id) {
        return
      }

      onSelectPersona(selectedPersona)
    },
    [activePersona?.id, onSelectPersona, personas]
  )

  return useMemo((): MenuItemConfig | undefined => {
    if (isFeatureMenuNotFound || arePersonasLoading || isActivePersonaLoading || !activePersona) {
      return undefined
    }

    return {
      key: 'role',
      title: t('suite.header.role'),
      subtitle: t(`suite.personas.${activePersona.name}`, {defaultValue: activePersona.name}),
      icon: mdiAccountCircleOutline,
      view: (
        <RoleSelectionMenu
          activePersonaId={activePersona.id}
          personas={personas}
          onSelectPersona={selectPersona}
          t={t}
        />
      )
    }
  }, [
    activePersona,
    arePersonasLoading,
    isActivePersonaLoading,
    isFeatureMenuNotFound,
    personas,
    selectPersona,
    t
  ])
}
